import { db, type Reminder } from '../db';
import { getActiveChildren, getChildById } from '../stores/children.svelte.ts';

function toPlain<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export async function createReminder(
  childId: string,
  type: Reminder['type'],
  intervalMs: number,
  label: string,
): Promise<Reminder> {
  const now = Date.now();
  const entry: Reminder = {
    id: crypto.randomUUID(),
    childId,
    type,
    intervalMs,
    nextFireAt: now + intervalMs,
    enabled: true,
    label,
    createdAt: now,
    updatedAt: now,
  };
  await db.reminders.add(toPlain(entry));
  return entry;
}

/** Reminders for a specific child — used by the management list page. */
export async function getActiveRemindersForChild(childId: string): Promise<Reminder[]> {
  const all = await db.reminders.where('childId').equals(childId).toArray();
  return all.filter((r) => r.enabled);
}

export async function getAllRemindersForChild(childId: string): Promise<Reminder[]> {
  return await db.reminders.where('childId').equals(childId).toArray();
}

/** All enabled reminders across non-archived children — used by the firing tick. */
export async function getAllActiveReminders(): Promise<Reminder[]> {
  const nonArchivedIds = new Set(getActiveChildren().map((c) => c.id));
  const all = await db.reminders.toArray();
  return all.filter((r) => r.enabled && nonArchivedIds.has(r.childId));
}

export async function toggleReminder(id: string, enabled: boolean): Promise<void> {
  const now = Date.now();
  const reminder = await db.reminders.get(id);
  if (!reminder) return;
  await db.reminders.update(id, {
    enabled,
    nextFireAt: enabled ? now + reminder.intervalMs : reminder.nextFireAt,
    updatedAt: now,
  });
}

export async function deleteReminder(id: string): Promise<void> {
  await db.reminders.delete(id);
}

export async function rescheduleReminder(id: string): Promise<void> {
  const reminder = await db.reminders.get(id);
  if (!reminder) return;
  await db.reminders.update(id, {
    nextFireAt: Date.now() + reminder.intervalMs,
    updatedAt: Date.now(),
  });
}

export async function snoozeReminder(id: string, snoozeMs: number = 15 * 60 * 1000): Promise<void> {
  await db.reminders.update(id, {
    nextFireAt: Date.now() + snoozeMs,
    updatedAt: Date.now(),
  });
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function getReminderRoute(type: Reminder['type']): string {
  if (type === 'pump') return '/session/pumping';
  if (type === 'feed') return '/session/nursing';
  return '/reminders';
}

export function showNotification(title: string, body: string, action?: string): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const options = {
    body,
    icon: '/android-chrome-192x192.png',
    data: { action: action || '/' },
  };

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((reg) => reg.showNotification(title, options));
  } else {
    const notif = new Notification(title, options);
    notif.onclick = () => {
      window.focus();
      window.history.pushState({}, '', action || '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
  }
}

export async function checkAndFireReminders(): Promise<Reminder[]> {
  const now = Date.now();
  const active = await getAllActiveReminders();
  const fired: Reminder[] = [];
  const showNames = getActiveChildren().length > 1;

  for (const reminder of active) {
    if (reminder.nextFireAt <= now) {
      const childName = showNames ? getChildById(reminder.childId)?.name : undefined;
      const title = childName ? `${reminder.label} — ${childName}` : reminder.label;
      showNotification(title, 'Time for a gentle reminder', getReminderRoute(reminder.type));
      await rescheduleReminder(reminder.id);
      fired.push(reminder);
    }
  }

  return fired;
}

export function getNextReminderTime(reminders: Reminder[]): number | null {
  const enabled = reminders.filter((r) => r.enabled);
  if (enabled.length === 0) return null;
  return Math.min(...enabled.map((r) => r.nextFireAt));
}
